import React from 'react';
import { ChatLog } from './MessageBubble';
import ChatInput from './ChatInput';
import ChatScopeBadge from './ChatScopeBadge';
import ThreadList from './ThreadList';
import type { ChatLayout, ChatMessage } from './types';
import type { AgentChatScope } from '../../services/api';
import { suggestedQuestions } from './demoData';
import { useOptionalOrganization } from '../../contexts/OrganizationContext';
import { useSavedReports } from '../reports/useSavedReports';

// PHA-1168: saved reports offered as `#handle` chips next to the suggested questions.
const MAX_REPORT_CHIPS = 4;

interface ChatProps {
  messages: ChatMessage[];
  isProcessing: boolean;
  onSend: (text: string) => void;
  onNewChat: () => void;
  onDecideApproval?: (approvalId: string, approved: boolean) => void;
  // PHA-1155: stop the answer being worked on.
  onStop?: () => void;
  onClose?: () => void;
  compact?: boolean;
  scope?: AgentChatScope | null;
  // PHA-1155: workspace layout + thread history. All optional so Chat still
  // renders on its own (as it did before) when a caller does not wire them.
  layout?: ChatLayout;
  onChangeLayout?: (layout: ChatLayout) => void;
  historyOpen?: boolean;
  onToggleHistory?: () => void;
  activeConversationKey?: string | null;
  onSelectThread?: (conversationKey: string) => void;
  historyRefreshToken?: number;
  loadingThread?: boolean;
}

const Chat: React.FC<ChatProps> = ({
  messages,
  isProcessing,
  onSend,
  onNewChat,
  onDecideApproval,
  onStop,
  onClose,
  compact,
  scope,
  layout = 'panel',
  onChangeLayout,
  historyOpen,
  onToggleHistory,
  activeConversationKey,
  onSelectThread,
  historyRefreshToken,
  loadingThread,
}) => {
  const org = useOptionalOrganization();
  // Fetched live, so a newly saved report shows up with no code change.
  const savedReports = useSavedReports(org?.orgId ?? '', { enabled: messages.length === 0 });
  const reportChips = savedReports.reports.slice(0, MAX_REPORT_CHIPS);
  const canShowHistory = !!onSelectThread;
  // Full screen keeps History docked; the narrower layouts open it on demand.
  const historyDocked = canShowHistory && layout === 'full';
  const historyOverlay = canShowHistory && !historyDocked && !!historyOpen;
  const wide = layout !== 'panel';

  const threadList = canShowHistory ? (
    <ThreadList
      activeKey={activeConversationKey}
      onSelect={(key) => onSelectThread!(key)}
      onNewChat={onNewChat}
      refreshToken={historyRefreshToken}
    />
  ) : null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#fff',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>🤖</span>
          <span style={{ fontWeight: 600, fontSize: 16, color: '#0f172a' }}>
            Phanova AI
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canShowHistory && !historyDocked && onToggleHistory && (
            <IconButton
              onClick={onToggleHistory}
              ariaLabel={historyOpen ? 'Hide history' : 'Show history'}
              title="History"
              active={!!historyOpen}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M3.5 10a6.5 6.5 0 1 0 1.9-4.6M3.5 3.5v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 6.5V10l2.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </IconButton>
          )}
          {onChangeLayout && layout !== 'panel' && (
            <IconButton onClick={() => onChangeLayout('panel')} ariaLabel="Compact panel" title="Compact panel">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="10.5" y="9.5" width="6" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
                <path d="M3.5 5.5h13M3.5 3.5v13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </IconButton>
          )}
          {onChangeLayout && layout !== 'side' && (
            <IconButton onClick={() => onChangeLayout('side')} ariaLabel="Side panel" title="Side panel">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="3.5" width="14" height="13" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
                <path d="M11 3.5v13" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </IconButton>
          )}
          {onChangeLayout && layout !== 'full' && (
            <IconButton onClick={() => onChangeLayout('full')} ariaLabel="Full screen" title="Full screen">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M4 8V4h4M16 8V4h-4M4 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </IconButton>
          )}
          <IconButton
            onClick={onNewChat}
            ariaLabel="New chat"
            title="New chat"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M4 15.5V17h1.5l9-9-1.5-1.5-9 9ZM15.6 5.4l1-1a1 1 0 0 1 1.4 0l.6.6a1 1 0 0 1 0 1.4l-1 1-2-2Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M3 18h14"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>
          {onClose && (
            <IconButton onClick={onClose} ariaLabel="Close chat" title="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M3 3L11 11M11 3L3 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
      {historyDocked && (
        <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #e2e8f0', background: '#fbfcfd' }}>
          {threadList}
        </div>
      )}
      {historyOverlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            background: '#fff',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          {threadList}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <ChatScopeBadge scope={scope ?? null} />

      {loadingThread && (
        <div style={{ padding: '10px 24px', fontSize: 13, color: '#64748b' }}>Opening conversation…</div>
      )}

      {/* Empty state */}
      {messages.length === 0 && !loadingThread && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          padding: '40px 24px',
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#e8f5e9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: 32 }}>🤖</span>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: '#0f172a' }}>
            How can I help?
          </h2>
          <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: 600,
          }}>
            {suggestedQuestions.map((q) => (
              <button
                key={q}
                onClick={() => onSend(q)}
                disabled={isProcessing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 20,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  color: '#475569',
                  fontSize: 13,
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontWeight: 400,
                  opacity: isProcessing ? 0.6 : 1,
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isProcessing) e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
              >
                <span style={{ fontWeight: 600, color: '#1976d2' }}>{'>'}</span>
                {q}
              </button>
            ))}
            {reportChips.map((r) => (
              <button
                key={`report-${r.id}`}
                onClick={() => onSend(`#${r.handle}`)}
                disabled={isProcessing}
                title={r.description ? `${r.name}: ${r.description}` : r.name}
                aria-label={`Run report ${r.name}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 20,
                  border: '1px solid #bae6fd',
                  backgroundColor: '#f0f9ff',
                  color: '#0369a1',
                  fontSize: 13,
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontWeight: 400,
                  opacity: isProcessing ? 0.6 : 1,
                }}
              >
                <span style={{ fontWeight: 600 }}>#</span>
                {r.handle}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message log */}
      {messages.length > 0 && (
        <ChatLog
          messages={messages}
          onDecideApproval={onDecideApproval}
          approvalBusy={isProcessing}
          wide={wide}
          onAnswer={onSend}
        />
      )}

      {/* Input */}
      <div style={wide ? { width: '100%', maxWidth: 1036, margin: '0 auto' } : undefined}>
        <ChatInput
          onSend={onSend}
          disabled={isProcessing || !!loadingThread}
          onStop={isProcessing ? onStop : undefined}
          compact={compact}
          autoFocus={compact}
        />
      </div>
      </div>
      </div>
    </div>
  );
};

// Compact icon button shared by the new-chat and close buttons so they match.
const IconButton: React.FC<{
  onClick: () => void;
  ariaLabel: string;
  title: string;
  active?: boolean;
  children: React.ReactNode;
}> = ({ onClick, ariaLabel, title, active, children }) => (
  <button
    onClick={onClick}
    type="button"
    title={title}
    aria-label={ariaLabel}
    aria-pressed={active === undefined ? undefined : active}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 8,
      border: '1px solid ' + (active ? '#bfdbfe' : '#e2e8f0'),
      backgroundColor: active ? '#eff6ff' : '#fff',
      color: active ? '#1d4ed8' : '#64748b',
      cursor: 'pointer',
      transition: 'background-color 120ms ease, color 120ms ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f8fafc';
      e.currentTarget.style.color = '#0f172a';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = active ? '#eff6ff' : '#fff';
      e.currentTarget.style.color = active ? '#1d4ed8' : '#64748b';
    }}
  >
    {children}
  </button>
);

export default Chat;
